import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight,
  Heart,
  ShoppingBag,
  MessageCircle,
  Ruler,
  ShieldCheck,
  Images,
  Headphones,
  Lock,
  ArrowRight,
} from "lucide-react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Gallery } from "../components/pdp/Gallery";
import { ProductCard } from "../components/vanities/ProductCard";
import { Reveal } from "../components/motion/Reveal";
import { toast } from "../components/ui/sonner";
import { useCart, cartLineKey } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useProductDetail, useRelated, useAddons } from "../lib/useApi";
import { AddOnDrawer } from "../components/AddOnDrawer";
import { formatPrice } from "../lib/filters";
import { WHATSAPP_URL } from "../lib/site";

// — small building blocks —
const PillGroup = ({ label, options, value, onChange, testid, format = (v) => v }) => (
  <div data-testid={testid}>
    <p className="eyebrow text-taupe mb-3">{label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            data-testid={`${testid}-${String(opt).replace(/[^a-zA-Z0-9]/g, "")}`}
            aria-pressed={active}
            className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${
              active
                ? "bg-charcoal text-ivory border-charcoal"
                : "bg-transparent text-charcoal border-beige hover:border-charcoal"
            }`}
          >
            {format(opt)}
          </button>
        );
      })}
    </div>
  </div>
);

const MetaChip = ({ children }) => (
  <span className="text-[0.72rem] text-charcoal/75 bg-stone/70 border border-beige/70 px-3 py-1.5 rounded-full">
    {children}
  </span>
);

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-3.5 border-b border-beige/70">
    <span className="text-sm text-taupe">{label}</span>
    <span className="text-sm text-charcoal font-medium text-right">{value}</span>
  </div>
);

const TRUST = [
  { icon: Ruler, t: "Clear dimensions" },
  { icon: ShieldCheck, t: "Premium finish" },
  { icon: Images, t: "High-quality visuals" },
  { icon: Headphones, t: "Support before purchase" },
  { icon: Lock, t: "Secure checkout" },
];

const NotFound = () => (
  <div className="min-h-screen bg-ivory">
    <Header />
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-40 pb-32 text-center">
      <h1 className="font-serif text-5xl text-charcoal">Vanity not found</h1>
      <p className="mt-4 text-charcoal/65">This piece may have moved. Explore the full collection instead.</p>
      <Link to="/vanities" className="mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal text-ivory px-7 py-3.5 text-sm font-medium">
        Browse all vanities <ArrowRight size={16} strokeWidth={1.5} />
      </Link>
    </div>
    <Footer />
  </div>
);

const PdpLoading = () => (
  <div data-testid="pdp-loading" className="min-h-screen bg-ivory animate-pulse">
    <Header />
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-40 pb-28 grid lg:grid-cols-2 gap-10 lg:gap-16">
      <div className="aspect-[4/5] w-full bg-stone rounded-sm" />
      <div className="space-y-4">
        <div className="h-4 w-24 bg-stone" />
        <div className="h-10 w-3/4 bg-stone" />
        <div className="h-8 w-32 bg-stone" />
        <div className="h-4 w-full bg-stone" />
        <div className="h-4 w-5/6 bg-stone" />
        <div className="h-16 w-full bg-stone rounded-full mt-8" />
      </div>
    </div>
  </div>
);

const defaultSelection = (product) => {
  const vs = product?.variantList ?? [];
  if (vs.length > 0) {
    const cheapest = [...vs].sort((a, b) => a.price - b.price)[0];
    return { width: cheapest.width, finish: cheapest.finish, sink: cheapest.sink };
  }
  return {
    width: product?.variants?.widths?.[0] ?? product?.width ?? null,
    finish: product?.variants?.finishes?.[0] ?? product?.finish ?? null,
    sink: product?.variants?.sinks?.[0] ?? product?.sink ?? null,
  };
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { data: product, isLoading: productLoading } = useProductDetail(id);
  const related = useRelated(id);
  const relatedItems = related.data?.items ?? [];
  const addons = useAddons(id);
  const addonData = addons.data;

  const [width, setWidth] = useState(product?.width);
  const [finish, setFinish] = useState(product?.finish);
  const [sink, setSink] = useState(product?.sink);
  const [addonOpen, setAddonOpen] = useState(false);
  const [parentLid, setParentLid] = useState(null);

  // Reset selections only when navigating to a new product (keyed by id).
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (product) {
      const defaults = defaultSelection(product);
      setWidth(defaults.width);
      setFinish(defaults.finish);
      setSink(defaults.sink);
    }
  }, [product?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (productLoading) return <PdpLoading />;
  if (!product) return <NotFound />;

  const wished = has(product.id);

  const selectedPrice = (() => {
    const vs = product.variantList ?? [];
    const norm = (x) => (x == null ? null : String(x));
    const exact = vs.find(
      (v) => norm(v.width) === norm(width) && norm(v.finish) === norm(finish) && norm(v.sink) === norm(sink)
    );
    if (exact) return exact.price;
    const byWidth = vs.find((v) => norm(v.width) === norm(width));
    if (byWidth) return byWidth.price;
    return vs[0]?.price ?? product.price;
  })();

  const keyDetails = [
    product.mounting ? ["Mounting type", product.mounting] : null,
    width != null ? ["Width", `${width}"`] : null,
    sink ? ["Sink type", sink] : null,
    product.style ? ["Style", product.style] : null,
    finish ? ["Finish / colour", finish] : null,
    product.basin ? ["Basin", product.basin] : null,
    product.storage ? ["Storage", product.storage] : null,
    product.idealSize ? ["Ideal bathroom size", product.idealSize] : null,
  ].filter(Boolean);

  const cartLine = () => ({
    id: product.id,
    name: product.name,
    price: selectedPrice,
    image: product.image,
    mounting: product.mounting,
    style: product.style,
    width,
    finish,
    sink,
  });

  const addToCart = () => {
    const line = cartLine();
    addItem(line);
    const lid = cartLineKey(line);
    const { taps, waste } = addonData || { taps: [], waste: null };
    if (taps.length || waste) {
      setParentLid(lid);
      setAddonOpen(true);
    } else {
      toast.success(`${product.name} added to cart`, {
        description: `${width}" · ${finish} · ${sink}`,
      });
    }
  };
  const buyNow = () => {
    addItem(cartLine());
    navigate("/cart");
  };
  const toggleWish = () => {
    toggle({
      id: product.id,
      name: product.name,
      price: selectedPrice,
      image: product.image,
      mounting: product.mounting,
      style: product.style,
      width,
      finish,
      sink,
    });
    toast(wished ? "Removed from wishlist" : "Saved to wishlist", { description: product.name });
  };

  const categoryHref =
    product.mounting === "Wall Mounted"
      ? "/vanities?mount=wall"
      : product.mounting === "Floor Mounted"
      ? "/vanities?mount=floor"
      : "/vanities";

  return (
    <div data-testid="pdp-page" className="bg-ivory">
      <Header />

      <main className="pt-28 md:pt-32 pb-28 lg:pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <nav data-testid="pdp-breadcrumb" aria-label="Breadcrumb" className="flex items-center flex-wrap gap-2 text-xs text-taupe mb-8">
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link to="/vanities" className="hover:text-charcoal transition-colors">Bathroom Vanities</Link>
            <ChevronRight size={12} />
            {product.mounting && (
              <>
                <Link to={categoryHref} className="hover:text-charcoal transition-colors">{product.mounting}</Link>
                <ChevronRight size={12} />
              </>
            )}
            <span className="text-charcoal">{product.name}</span>
          </nav>

          {/* Two-column */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Left column: gallery + key details (keeps the right panel pinned longer) */}
            <div className="flex flex-col gap-14">
              <Gallery key={product.id} gallery={product.gallery} productName={product.name} />

              {/* Key Details */}
              <div data-testid="pdp-key-details">
                <p className="eyebrow text-taupe mb-4">Key Details</p>
                <div className="w-full">
                  {keyDetails.map(([label, value]) => (
                    <DetailRow key={label} label={label} value={value} />
                  ))}
                </div>
              </div>
            </div>

            {/* Purchase panel */}
            <div className="lg:sticky lg:top-28 lg:self-start">
              {product.badges.length > 0 && (
                <span className="eyebrow text-taupe">{product.badges[0]}</span>
              )}
              <h1 data-testid="pdp-title" className="font-serif font-light text-charcoal text-4xl md:text-5xl leading-[1.05] tracking-tight mt-2">
                {product.name}
              </h1>

              <p data-testid="pdp-price" className="font-serif text-3xl text-charcoal mt-5">
                {formatPrice(selectedPrice)}
              </p>

              {/* Compact metadata row */}
              <div data-testid="pdp-metadata" className="flex flex-wrap gap-2 mt-5">
                {[product.mounting, width != null ? `${width}" Width` : null, sink, product.style]
                  .filter(Boolean)
                  .map((chip) => (
                    <MetaChip key={chip}>{chip}</MetaChip>
                  ))}
              </div>

              <p className="mt-6 text-charcoal/70 leading-relaxed max-w-xl">{product.shortDesc}</p>

              {/* Variant selectors */}
              <div className="mt-9 space-y-7">
                {product.variants.widths.length > 0 && (
                  <PillGroup
                    label="Width"
                    testid="variant-width"
                    options={product.variants.widths}
                    value={width}
                    onChange={setWidth}
                    format={(v) => `${v}"`}
                  />
                )}
                {product.variants.finishes.length > 0 && (
                  <PillGroup
                    label="Finish / Colour"
                    testid="variant-finish"
                    options={product.variants.finishes}
                    value={finish}
                    onChange={setFinish}
                  />
                )}
                {product.variants.sinks.length > 0 && (
                  <PillGroup
                    label="Sink Type"
                    testid="variant-sink"
                    options={product.variants.sinks}
                    value={sink}
                    onChange={setSink}
                  />
                )}
              </div>

              {/* CTAs */}
              <div className="mt-9 flex flex-col gap-3">
                <div className="flex gap-3">
                  <button
                    onClick={addToCart}
                    data-testid="pdp-add-to-cart"
                    className="group flex-1 inline-flex items-center justify-center gap-3 rounded-full bg-charcoal text-ivory px-7 py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
                  >
                    <ShoppingBag size={17} strokeWidth={1.6} />
                    Add to Cart
                  </button>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleWish}
                    data-testid="pdp-wishlist"
                    aria-pressed={wished}
                    aria-label="Save to wishlist"
                    className={`grid place-items-center h-[52px] w-[52px] rounded-full border transition-colors duration-300 ${wished ? "border-charcoal bg-stone/60" : "border-beige hover:border-charcoal"} text-charcoal`}
                  >
                    <Heart size={19} strokeWidth={1.6} className={wished ? "fill-charcoal" : ""} />
                  </motion.button>
                </div>
                <button
                  onClick={buyNow}
                  data-testid="pdp-buy-now"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-charcoal px-7 py-4 text-sm font-medium text-charcoal hover:bg-stone/60 transition-colors duration-300"
                >
                  Buy Now
                </button>
                <a
                  href={WHATSAPP_URL(`Hi BathLane, I'd like help choosing the ${product.name}.`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pdp-whatsapp"
                  className="mt-1 inline-flex items-center justify-center gap-2 text-sm font-medium text-charcoal link-sweep w-fit mx-auto"
                >
                  <MessageCircle size={16} strokeWidth={1.5} />
                  Ask on WhatsApp — need help choosing size?
                </a>
              </div>

              {/* Quick reassurance */}
              <div className="mt-8 pt-6 border-t border-beige grid grid-cols-2 gap-y-3 gap-x-4">
                {product.depth != null && (
                  <p className="text-xs text-charcoal/70 flex items-center gap-2"><Ruler size={14} strokeWidth={1.5} className="text-taupe" /> {product.depth}" deep · {product.height}" high</p>
                )}
                {product.basin && (
                  <p className="text-xs text-charcoal/70 flex items-center gap-2"><ShieldCheck size={14} strokeWidth={1.5} className="text-taupe" /> {product.basin}</p>
                )}
                <p className="text-xs text-charcoal/70 flex items-center gap-2"><Lock size={14} strokeWidth={1.5} className="text-taupe" /> Secure checkout</p>
                <p className="text-xs text-charcoal/70 flex items-center gap-2"><Headphones size={14} strokeWidth={1.5} className="text-taupe" /> Support before you buy</p>
              </div>
            </div>
          </div>

          {/* ── Dimensions (prominent) ── */}
          {product.kind === "vanity" && (
          <section data-testid="pdp-dimensions" className="mt-24 md:mt-32">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center bg-stone rounded-sm p-8 md:p-14">
              <div>
                <p className="eyebrow text-taupe mb-4 flex items-center gap-2"><Ruler size={15} strokeWidth={1.5} /> Dimensions</p>
                <h2 className="font-serif font-light text-charcoal text-3xl md:text-5xl leading-[1.05] tracking-tight">
                  Built to fit, measured with care.
                </h2>
                <div className="mt-8 max-w-sm">
                  <DetailRow label="Width" value={`${width}"`} />
                  <DetailRow label="Depth" value={`${product.depth}"`} />
                  <DetailRow label="Height" value={`${product.height}"`} />
                </div>
                <p className="mt-6 text-sm text-charcoal/65 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-taupe" />
                  Please measure your bathroom space before ordering.
                </p>
              </div>

              {/* Diagram placeholder */}
              <div className="relative aspect-[4/3] rounded-sm border border-beige bg-ivory p-8 md:p-12">
                <div className="relative h-full w-full border border-dashed border-taupe/50 rounded-sm flex items-center justify-center">
                  <span className="font-serif text-taupe/70 text-lg">{product.name}</span>
                  <div className="absolute -top-4 left-0 right-0 flex items-center gap-2">
                    <span className="h-px flex-1 bg-taupe/40" />
                    <span className="eyebrow text-taupe">{width}" W</span>
                    <span className="h-px flex-1 bg-taupe/40" />
                  </div>
                  <div className="absolute top-0 bottom-0 -right-4 flex flex-col items-center gap-2">
                    <span className="w-px flex-1 bg-taupe/40" />
                    <span className="eyebrow text-taupe [writing-mode:vertical-rl]">{product.height}" H</span>
                    <span className="w-px flex-1 bg-taupe/40" />
                  </div>
                  <span className="absolute bottom-3 left-3 eyebrow text-taupe/70">{product.depth}" D</span>
                </div>
              </div>
            </div>
          </section>
          )}

          {/* ── Why this vanity works ── */}
          {product.story.length > 0 && (
          <section data-testid="pdp-story" className="mt-24 md:mt-32">
            <p className="eyebrow text-taupe mb-4">Why this vanity works</p>
            <h2 className="font-serif font-light text-charcoal text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-2xl">
              Designed for real bathrooms, not showrooms.
            </h2>
            <div className="mt-14 grid md:grid-cols-3 gap-px bg-beige border border-beige rounded-sm overflow-hidden">
              {product.story.map((s, i) => (
                <Reveal key={s.h} delay={i * 0.08} className="bg-ivory p-8 md:p-9">
                  <span className="font-serif text-taupe text-2xl">0{i + 1}</span>
                  <h3 className="font-serif text-2xl text-charcoal mt-5 leading-tight">{s.h}</h3>
                  <p className="mt-4 text-sm text-charcoal/70 leading-relaxed">{s.p}</p>
                </Reveal>
              ))}
            </div>
          </section>
          )}

          {/* ── Trust strip ── */}
          <section data-testid="pdp-trust" className="mt-20">
            <div className="grid grid-cols-2 md:grid-cols-5 border border-beige rounded-sm overflow-hidden">
              {TRUST.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.t}
                    className={`p-6 flex flex-col items-center text-center gap-3 ${i !== 0 ? "md:border-l border-beige" : ""} ${i >= 2 && i < 4 ? "border-t md:border-t-0 border-beige" : ""} ${i >= 4 ? "border-t md:border-t-0 border-beige" : ""} ${i % 2 === 1 ? "border-l md:border-l" : ""}`}
                  >
                    <Icon size={22} strokeWidth={1.3} className="text-charcoal" />
                    <span className="text-xs text-charcoal/75 leading-snug">{item.t}</span>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Need help choosing ── */}
          <section data-testid="pdp-help" className="mt-20 bg-charcoal text-ivory rounded-sm p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="max-w-xl">
              <p className="eyebrow text-taupe mb-3">Need help choosing?</p>
              <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight">
                Unsure if this size fits your bathroom?
              </h2>
              <p className="mt-4 text-ivory/70 leading-relaxed">
                Share your wall width and clearances — we'll confirm the right fit before you order.
              </p>
            </div>
            <a
              href={WHATSAPP_URL(`Hi BathLane, I need size help for the ${product.name}.`)}
              target="_blank"
              rel="noreferrer"
              data-testid="pdp-help-whatsapp"
              className="shrink-0 inline-flex items-center justify-center gap-3 rounded-full bg-ivory text-charcoal px-8 py-4 text-sm font-medium transition-transform duration-300 hover:-translate-y-0.5"
            >
              <MessageCircle size={17} strokeWidth={1.6} />
              Get help on WhatsApp
            </a>
          </section>

          {/* ── Related products ── */}
          <section data-testid="pdp-related" className="mt-24 md:mt-32">
            <div className="flex items-end justify-between gap-6 mb-12">
              <h2 className="font-serif font-light text-charcoal text-3xl md:text-5xl leading-[1.05] tracking-tight">
                You may also like
              </h2>
              <Link to={categoryHref} className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-charcoal link-sweep whitespace-nowrap">
                View {product.mounting} <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {relatedItems.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />

      {/* Mobile sticky CTA bar */}
      <div
        data-testid="pdp-sticky-cta"
        className="fixed bottom-0 inset-x-0 z-50 lg:hidden bg-ivory/97 backdrop-blur-sm border-t border-beige px-4 py-3 flex items-center justify-between gap-4"
      >
        <div>
          <p className="text-[0.68rem] text-taupe leading-none">{product.name}</p>
          <p className="font-serif text-xl text-charcoal leading-tight">{formatPrice(selectedPrice)}</p>
        </div>
        <button
          onClick={addToCart}
          data-testid="pdp-sticky-add"
          className="inline-flex items-center gap-2 rounded-full bg-charcoal text-ivory px-7 py-3.5 text-sm font-medium"
        >
          <ShoppingBag size={16} strokeWidth={1.6} /> Add to Cart
        </button>
      </div>

      <AddOnDrawer
        open={addonOpen}
        onOpenChange={setAddonOpen}
        product={product}
        parentLid={parentLid}
        addons={addonData}
      />
    </div>
  );
}
